import { Tag, Percent, DollarSign, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { type BillingSubscription } from "@/services/superAdminBillingService";

type Props = {
  subscriptions: BillingSubscription[];
  loading?: boolean;
};

export default function PromotionsDisplay({ subscriptions, loading }: Props) {
  const activePromotions = subscriptions
    .filter((sub) => sub.discount)
    .map((sub) => ({
      subscriptionId: sub.id,
      status: sub.status,
      discount: sub.discount!,
    }));

  if (loading) {
    return <div className="h-32 bg-gray-100 rounded-lg animate-pulse" />;
  }

  if (activePromotions.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-500">
          No active promotions or discounts
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {activePromotions.map((promo) => (
        <Card key={promo.subscriptionId}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Tag className="h-4 w-4" />
                {promo.discount.coupon.name || promo.discount.coupon.id}
              </CardTitle>
              <Badge variant="secondary">{promo.status}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-gray-500 mb-1">Discount</div>
                <div className="font-medium flex items-center gap-1">
                  {promo.discount.coupon.percent_off ? (
                    <>
                      <Percent className="h-3 w-3" />
                      {promo.discount.coupon.percent_off}% off
                    </>
                  ) : promo.discount.coupon.amount_off ? (
                    <>
                      <DollarSign className="h-3 w-3" />$
                      {(promo.discount.coupon.amount_off / 100).toFixed(2)} off
                    </>
                  ) : (
                    "Custom"
                  )}
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Duration</div>
                <div className="font-medium flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {promo.discount.coupon.duration === "forever"
                    ? "Forever"
                    : promo.discount.coupon.duration === "once"
                      ? "Once"
                      : promo.discount.coupon.duration === "repeating"
                        ? "Repeating"
                        : promo.discount.coupon.duration}
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Applied to</div>
                <div className="font-mono text-xs">
                  {promo.subscriptionId.slice(0, 20)}...
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
